import { DailyPlayers } from '../../../api/reportingTypes'
import { t } from 'i18next'

export const YEAR = 365 * 24 * 60 * 60 * 1000
export const DAY = 24 * 60 * 60 * 1000
export const MONTH = 30 * DAY
export const NOW = () => new Date().getTime()

export type GraphData = {
  maxValue: number
  valueLabels: number[]
  barLabels: string[]
  barValues: number[]
}

export const EMPTY_GRAPH: GraphData = { valueLabels: [], barLabels: [], barValues: [], maxValue: 0 }

const getSum = (data: DailyPlayers, yearMonth: string) => {
  let sum = 0
  Object.keys(data)
    .filter((key) => {
      return key.startsWith(yearMonth)
    })
    .forEach((k) => {
      sum += data[k]
    })
  return sum
}

export const getMonthGraphData = (
  orgDailyPlayers: DailyPlayers,
  playerGraphStartDate: Date,
  playerGraphEndDate: Date,
): GraphData => {
  const data: GraphData = { valueLabels: [], barLabels: [], barValues: [], maxValue: 0 }
  const maxEndDate = new Date(playerGraphStartDate).setFullYear(playerGraphStartDate.getFullYear() + 2)
  let maxValue = 0
  let currentDataDate = new Date(playerGraphStartDate)

  while (currentDataDate <= new Date(Math.min(playerGraphEndDate.getTime(), maxEndDate))) {
    let currentYearMonth =
      currentDataDate.getFullYear() + (currentDataDate.getMonth() < 9 ? '-0' : '-') + (currentDataDate.getMonth() + 1)
    let currentYearMonthLabel =
      currentDataDate.getFullYear() + (currentDataDate.getMonth() < 9 ? '/0' : '/') + (currentDataDate.getMonth() + 1)
    data.barLabels.push(currentYearMonthLabel)
    const sum = getSum(orgDailyPlayers, currentYearMonth)
    data.barValues.push(sum)
    if (sum > maxValue) maxValue = sum
    currentDataDate.setMonth(currentDataDate.getMonth() + 1)
  }

  const rounder = maxValue > 100 ? 10 : 0
  data.valueLabels = [
    -rounder, //Rounding will make this zero
    maxValue / 4 - rounder,
    maxValue / 2 - rounder,
    (3 * maxValue) / 4 - rounder,
    maxValue - rounder,
  ]
  data.maxValue = maxValue
  return data
}

export const getDayGraphData = (
  orgDailyPlayers: DailyPlayers,
  playerGraphStartDate: Date,
  playerGraphEndDate: Date,
): GraphData => {
  const data: GraphData = { valueLabels: [], barLabels: [], barValues: [], maxValue: 0 }
  const maxEndDate = new Date(playerGraphStartDate).setFullYear(playerGraphStartDate.getFullYear() + 2)
  let maxValue = 0
  let currentDataDate = new Date(playerGraphStartDate)
  while (currentDataDate <= new Date(Math.min(playerGraphEndDate.getTime(), maxEndDate))) {
    let currentDay =
      currentDataDate.getFullYear() +
      (currentDataDate.getMonth() < 9 ? '-0' : '-') +
      (currentDataDate.getMonth() + 1) +
      (currentDataDate.getDate() < 10 ? '-0' : '-') +
      currentDataDate.getDate()

    let currentDayLabel =
      (currentDataDate.getDate() < 10 ? '0' : '') +
      currentDataDate.getDate() +
      (currentDataDate.getMonth() < 9 ? '/0' : '/') +
      (currentDataDate.getMonth() + 1)

    data.barLabels.push(currentDayLabel)
    const sum = getSum(orgDailyPlayers, currentDay)
    data.barValues.push(sum)
    if (sum > maxValue) maxValue = sum
    currentDataDate.setDate(currentDataDate.getDate() + 1)
  }

  data.valueLabels = [
    0,
    Math.floor(maxValue / 4),
    Math.floor(maxValue / 2),
    3 * Math.floor(maxValue / 4),
    Math.floor(maxValue),
  ]
  data.maxValue = maxValue
  return data
}

export const largeNumberFormat = (n?: number): string => {
  return !n
    ? ''
    : n
        .toString()
        .split('')
        .reverse()
        .join('')
        .replace(/(.{3})/g, '$1 ')
        .split('')
        .reverse()
        .join('')
}

export const OrgReportInsights = () => {
  return [
    t(
      'reports.general_insights.insight1',
      'Did you know? Games with interactive challenges get 3x more engagement! Try adding a quiz or a location-based task to your next game.',
    ),
    t(
      'reports.general_insights.insight2',
      'Need inspiration? Check out Seppo’s templates to jumpstart your next game and save time!',
    ),
    t(
      'reports.general_insights.insight3',
      'Fun fact: The most successful Seppo games have an engaging story. What’s the story of your next game?',
    ),
    t(
      'reports.general_insights.insight4',
      'A great game starts with a great hook! How will you surprise your players in the first 60 seconds?',
    ),
    t(
      'reports.general_insights.insight5',
      'The best way to learn Seppo is to experiment. Try adding a new game element today!',
    ),
    t(
      'reports.general_insights.insight6',
      'Remember: Short and engaging tasks keep players motivated. What’s your shortest task so far?',
    ),
    t(
      'reports.general_insights.insight7',
      'Boost engagement! Games with a mix of different task types keep players excited and learning.',
    ),
    t(
      'reports.general_insights.insight8',
      'Your game could be the next big hit! What’s one new idea you could test today?',
    ),
    t('reports.general_insights.insight9', 'Try adding a time limit to a task—can your players beat the clock?'),
    t(
      'reports.general_insights.insight10',
      'Games with teamwork elements get better feedback! Could you add a team challenge?',
    ),
    t(
      'reports.general_insights.insight11',
      'Did you know? Adding images or videos makes your game more immersive. Try it now!',
    ),
    t(
      'reports.general_insights.insight12',
      'A good game is never finished—keep iterating! What’s one thing you could improve in your latest game?',
    ),
    t(
      'reports.general_insights.insight13',
      'Challenge yourself: Can you create a game in under 10 minutes? Try it and see!',
    ),
    t(
      'reports.general_insights.insight14',
      'What if your next game had a secret mission? Mystery makes games even more fun!',
    ),
    t('reports.general_insights.insight15', 'Players love surprises! Can you add a twist to one of your tasks?'),
  ]
}
